import type {Skeleton,V3} from './rig';
import {evaluate,qEuler,qMul,type Pose,type Quat} from './pose';
import type {WeaponGrip} from './weapons';
const ease=(n:number)=>{const v=Math.max(0,Math.min(1,n));return v*v*(3-2*v);};
const blend=(a:Quat,b:Quat,t:number):Quat=>{const sign=a.reduce((s,v,i)=>s+v*b[i],0)<0?-1:1,q=a.map((v,i)=>v*(1-t)+b[i]*sign*t),l=Math.hypot(...q)||1;return q.map(v=>v/l) as Quat;};
const aim=(a:V3,b:V3):Quat=>{
 const al=Math.hypot(...a)||1,bl=Math.hypot(...b)||1,f=a.map(v=>v/al),t=b.map(v=>v/bl);
 const q:Quat=[f[1]*t[2]-f[2]*t[1],f[2]*t[0]-f[0]*t[2],f[0]*t[1]-f[1]*t[0],1+f.reduce((s,v,i)=>s+v*t[i],0)];
 const n=Math.hypot(...q)||1;return q.map(v=>v/n) as Quat;
};

/** Deterministic terminal poses. The runtime grounds the dressed mesh after
 * this motion, including the held prop, so a large graft cannot sink in stone. */
export function applyTerminalPose(sk:Skeleton,pose:Pose,action:'defeat'|'victory',time:number,grips:WeaponGrip[]=[]):void {
 const set=(name:string,x:number,y:number,z:number)=>{const i=sk.byName.get(name);if(i!==undefined)pose.bones[i].rot=qMul(pose.bones[i].rot,qEuler(x,y,z));};
 if(action==='victory'){
  const salute=ease(time/.6),settle=ease((time-.85)/.55);
  set('pelvis',0,-.13*salute,0);set('chest',-.11*salute,.18*salute,-.045*salute);set('head',-.1*salute,.16*salute,.06*salute);
  set('upperArmL',-.3*salute,0,-.5*salute);set('foreArmL',-.45*salute,0,-.45*salute);
  set('handL',0,0,.14*salute*(1-settle));
  return;
 }
 const buckle=ease(time/.23),fall=ease((time-.16)/.56);
 // End in a relaxed supine silhouette, rather than inheriting a weapon's
 // upright ready stance. In particular a straight supporting arm must not
 // become the lowest point and prop the whole dressed torso above the floor.
 const target=(name:string,q:Quat,w=fall)=>{const i=sk.byName.get(name);if(i!==undefined)pose.bones[i].rot=blend(pose.bones[i].rot,q,w);};
 set('pelvis',.12*buckle,0,0);
 target('pelvis',qEuler(-Math.PI/2,0,.04));target('chest',qEuler(.07,0,0));target('neck',qEuler(.05,0,0));target('head',qEuler(.08,0,.04));
 for(const side of ['L','R']){target('thigh'+side,qEuler(side==='L'?-.24:-.08,0,side==='L'?.14:0));target('shin'+side,qEuler(side==='L'?.38:.16,0,side==='L'?-.22:0));}
 if(fall>0)for(const side of ['L','R'])for(const part of ['upperArm','foreArm']){
  const i=sk.byName.get(part+side)!;
  const ev=evaluate(sk,pose),root=ev.world[sk.byName.get('pelvis')!],parent=ev.world[sk.bones[i].parent];
  const direction:V3=[side==='L'?-.12:.08,-.98,part==='upperArm'?.16:.28];
  const world=[0,1,2].map(r=>root[r]*direction[0]+root[3+r]*direction[1]+root[6+r]*direction[2]);
  const local=[0,1,2].map(c=>parent[c*3]*world[0]+parent[c*3+1]*world[1]+parent[c*3+2]*world[2]) as V3;
  const bone=sk.bones[i],rest=bone.tail.map((v,a)=>v-bone.head[a]) as V3;
  target(part+side,aim(rest,local));
 }
 // Once the actor has toppled, rest the weapon parallel to the floor. Rotate
 // the actual hand: the prop remains gripped instead of floating independently.
 if(fall>0){
  const ev=evaluate(sk,pose);
  for(const grip of grips){
   const hand=grip.palmBone??grip.bone,parent=sk.bones[hand].parent,frame=ev.world[parent];
   const forward:V3=[-.75,0,.66];
   const local=[0,1,2].map(i=>frame[i*3]*forward[0]+frame[i*3+1]*forward[1]+frame[i*3+2]*forward[2]) as V3;
   pose.bones[hand].rot=blend(pose.bones[hand].rot,aim(grip.across,local),fall);
  }
 }
}
